import { ComponentProps, ReactNode, useEffect, useState } from 'react';

import { Button } from '@/components/ui/button';
import { FetcherError } from 'utils/fetcher';

export const FidelityApplicationSection = ({
    name,
    title = `SMA ${name} Applications`,
    hasAccounts,
    groupCount,
    checkingAccess,
    ssnAccess,
    applications,
    children,
}: {
    name: string;
    title?: string;
    hasAccounts: boolean;
    groupCount: number | undefined;
    checkingAccess: boolean;
    ssnAccess: boolean;
    applications: { isPending: boolean; isError: boolean; refetch: () => unknown };
    children: ReactNode;
}) => {
    if (
        !hasAccounts ||
        (!checkingAccess && ssnAccess && !applications.isPending && !applications.isError && groupCount === 0)
    ) {
        return null;
    }
    return (
        <section className="flex flex-col gap-5" aria-label={title}>
            <div className="flex flex-col gap-2">
                <h2 className="font-heading text-xl">{title}</h2>
            </div>
            {checkingAccess ? (
                <p role="status">Checking access…</p>
            ) : !ssnAccess ? (
                <p role="status">SSN access is required to generate an application.</p>
            ) : applications.isPending ? (
                <p role="status">Preparing {name} applications…</p>
            ) : applications.isError ? (
                <div role="alert" className="flex items-center gap-3">
                    <p>Could not load {name} applications.</p>
                    <Button variant="outline" onClick={() => void applications.refetch()}>
                        Try again
                    </Button>
                </div>
            ) : (
                children
            )}
        </section>
    );
};

export const FidelityPacketCard = ({
    name,
    title = `SMA ${name} Application`,
    accountCount,
    action,
    children,
}: {
    name: string;
    title?: string;
    accountCount: number;
    action?: ReactNode;
    children: ReactNode;
}) => (
    <article className="border-border flex flex-col gap-4 rounded-lg border bg-white p-5">
        <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
                <h3 className="font-heading text-lg">{title}</h3>
                <p className="text-cocoa-500 text-sm">
                    {accountCount} {accountCount === 1 ? 'account' : 'accounts'} · Editable PDF draft
                </p>
            </div>
            {action}
        </div>
        {children}
    </article>
);

export const FidelityAccountList = ({
    accountGUIDs,
    title,
    children,
    action,
}: {
    accountGUIDs: string[];
    title: (guid: string) => string;
    children?: (guid: string) => ReactNode;
    action?: (guid: string) => ReactNode;
}) => {
    return (
        <ol className="space-y-3">
            {accountGUIDs.map((guid, index) => (
                <li
                    key={guid}
                    className="border-border flex flex-wrap items-center justify-between gap-4 border-t pt-3"
                >
                    <div className="min-w-0 flex-1">
                        <p className="text-cocoa-500 text-xs">Account {index + 1}</p>
                        <p className="text-cocoa-900 font-medium">{title(guid)}</p>
                        {children?.(guid)}
                    </div>
                    {action?.(guid)}
                </li>
            ))}
        </ol>
    );
};

export const FidelityCompletionRequirements = ({
    requirements,
    beforeChoices = false,
}: {
    requirements: string[];
    beforeChoices?: boolean;
}) => (
    <details>
        <summary className="cursor-pointer font-medium">Required completion and supporting documents</summary>
        <ul className="mt-2 list-inside list-disc space-y-2 text-sm">
            {requirements.map((reason) => (
                <li key={reason}>{reason}</li>
            ))}
        </ul>
        {beforeChoices && (
            <p className="mt-2 text-sm">
                This list describes saved data before the choices above. The downloaded checklist reflects your choices.
            </p>
        )}
    </details>
);

export const FidelityDownloadButton = ({
    download,
    active = true,
    disabled,
    children,
    ...props
}: ComponentProps<typeof Button> & {
    download: { isPending: boolean; isSuccess: boolean };
    active?: boolean;
}) => {
    const [confirmationExpired, setConfirmationExpired] = useState(false);
    const showConfirmation = active && download.isSuccess && !confirmationExpired;

    useEffect(() => {
        if (!download.isSuccess || !active) {
            setConfirmationExpired(false);
            return;
        }
        const timeout = setTimeout(() => setConfirmationExpired(true), 4000);
        return () => clearTimeout(timeout);
    }, [download.isSuccess, active]);

    return (
        <Button {...props} disabled={disabled || download.isPending || showConfirmation}>
            <span aria-live="polite">
                {active && download.isPending
                    ? 'Generating…'
                    : showConfirmation
                      ? 'Download started — check downloads'
                      : children}
            </span>
        </Button>
    );
};

export const FidelityDownloadFeedback = ({
    download,
    onRefresh,
}: {
    download: { isError: boolean; error: unknown };
    onRefresh?: () => void;
}) => (
    <>
        {download.isError && (
            <div role="alert" className="flex flex-wrap items-center gap-3">
                <p className="text-destructive text-sm">
                    {download.error instanceof FetcherError
                        ? download.error.message
                        : 'Could not generate the application. Review saved details and draft choices, then try again.'}
                </p>
                {onRefresh && (
                    <Button variant="outline" onClick={onRefresh}>
                        Refresh applications
                    </Button>
                )}
            </div>
        )}
    </>
);
